// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-01-22 03:21:45 +0100 (Thu, 22 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 159936 $
//  Purpose   : setup part registry and load global parts
//              also wiki-templates, jobs, dashpanels and columns
// =============================================================================
// ---Third-party imports
import React, {Component, Fragment} from 'react'
import {isValidElementType, isElement, isFragment} from 'react-is'

// ---Framework imports
import {each, size, keys, items, mapItems, iterItems, dict, merge} from 'ldash'

import log from 'uix/logging'

log.info('setup registries (parts, templates, jobs, dashpanels, columns)')

export {Part, funcPart, StandalonePart, createParts, createPart} from './part'

import {Part, UnknownPart, funcPart} from './part'

const coreParts = {UnknownPart}

import {WikiTemplates, JobTemplates, DashboardPanels, NodeMenuItems, Parts} from 'uix/registry'

export {loadLazy} from 'uix/utils'

// ---Globals

let normalizeParts = parts =>
  /* normalize raw react types to funcParts
   */
  dict(
    mapItems(parts, (n, p) => {
      let isClassPart = p.prototype instanceof Part
      let isFuncPart = p.__render_f // funcPart() generated
      if (isClassPart || isFuncPart) {
        return [n, p]
      } else if (isValidElementType(p)) {
        // auto transform valid react types to simple part funcs
        return [n, funcPart(props => React.createElement(p, props))]
      } else if (isElement(p)) {
        // also raw fragments
        return [n, funcPart(() => p)]
      } else {
        throw Error(`exported invalid Part "${n}" in uix/${modName}/parts`)
      }
    })
  )

Parts.register(normalizeParts(coreParts))

import {registerColumns} from 'uix/db/columns'

import {
  partModules,
  templateModules,
  jobModules,
  dashboardModules,
  nodeMenuModules,
  columnModules,
  partProperties,
  nestedModuleNames,
} from 'uix/registries'

// we read and store part props before we wrap the part via connect() HOC
let readProps = parts =>
  iterItems(parts, (partType, partDesc) => {
    partProperties[partType] = {
      getGraphics: partDesc.getGraphics || [],
      getChunkedUtils: partDesc.getChunkedUtils || [],
      filters: partDesc.filters || [],
    }
    //~ if (partType.includes('State')) log('part %s: %o', partType, partDesc.getGraphics)
  })

readProps(coreParts)

let moduleNames = keys({
  ...partModules,
  ...templateModules,
  ...jobModules,
  ...dashboardModules,
  ...nodeMenuModules,
})

const N = name => {
  if (nestedModuleNames[name]) {
    return `modules/${name}`
  }
  return `${name}`
}
// setup custom imports, key is importable filename in module
// isActive could also be a function, receiving the filename
const registrations = {
  parts: {
    isActive: partModules,
    register: parts => {
      parts = normalizeParts(parts)
      Parts.register(parts)
      readProps(parts)
    },
  },
  templates: {isActive: templateModules, register: templates => merge(WikiTemplates, templates)},
  jobs: {isActive: jobModules, register: jobs => merge(JobTemplates, jobs)},
  dashpanels: {isActive: dashboardModules, register: panels => merge(DashboardPanels, panels)},
  nodemenu: {isActive: nodeMenuModules, register: menutitems => merge(NodeMenuItems, menutitems)},
  columns: {
    isActive: columnModules,
    register: columns => registerColumns(columns),
  },
}

const promises = []
const loadedPromises = []
// XXX: also see https://github.com/webpack/webpack/issues/5029
// for prod/dev hot-reload aspect
// 2023-01-23: hot-reload is no longer a problem after switching to fast-refresh
// but require() is still needed for SSR build
const loadModule = (mod, filename, cfg) => {
  let objects = null
  let p = null
  let getObjects = module => module // assume module exports a dictionary, register will probably merge
  // we need to spell out the imports here
  if (filename == 'parts') {
    // #if process.env.UIX_PROD_CHUNKED !== 'true'
//     objects = require(`uix/${N(mod)}/parts`)
    // #endif

    // #if process.env.UIX_PROD_CHUNKED === 'true'
    p = import(/* webpackChunkName: 'core' */ /* webpackMode: "lazy-once" */ `uix/${N(mod)}/parts`)
    // #endif
  } else if (filename == 'templates') {
    // #if process.env.UIX_PROD_CHUNKED !== 'true'
//     objects = require(`uix/${N(mod)}/templates`)
    // #endif

    // #if process.env.UIX_PROD_CHUNKED === 'true'
    p = import(
      /* webpackChunkName: 'core' */ /* webpackMode: "lazy-once" */ `uix/${N(mod)}/templates`
    )
    // #endif
  } else if (filename == 'jobs') {
    // #if process.env.UIX_PROD_CHUNKED !== 'true'
//     objects = require(`uix/${N(mod)}/jobs`)
    // #endif

    // #if process.env.UIX_PROD_CHUNKED === 'true'
    p = import(/* webpackChunkName: 'core' */ /* webpackMode: "lazy-once" */ `uix/${N(mod)}/jobs`)
    // #endif
  } else if (filename == 'dashpanels') {
    // #if process.env.UIX_PROD_CHUNKED !== 'true'
//     objects = require(`uix/${N(mod)}/dashpanels`)
    // #endif

    // #if process.env.UIX_PROD_CHUNKED === 'true'
    p = import(
      /* webpackChunkName: 'core' */ /* webpackMode: "lazy-once" */ `uix/${N(mod)}/dashpanels`
    )
    // #endif
  } else if (filename == 'nodemenu') {
    // #if process.env.UIX_PROD_CHUNKED !== 'true'
//     objects = require(`uix/${N(mod)}/nodemenu`)
    // #endif

    // #if process.env.UIX_PROD_CHUNKED === 'true'
    p = import(
      /* webpackChunkName: 'core' */ /* webpackMode: "lazy-once" */ `uix/${N(mod)}/nodemenu`
    )
    // #endif
  } else if (filename == 'columns') {
    // #if process.env.UIX_PROD_CHUNKED !== 'true'
//     objects = require(`uix/${N(mod)}/columns`).columns
    // #endif

    // #if process.env.UIX_PROD_CHUNKED === 'true'
    p = import(
      /* webpackChunkName: 'core' */ /* webpackMode: "lazy-once" */ `uix/${N(mod)}/columns`
    )
    getObjects = module => module.columns
    // #endif
  }
  if (objects) {
    cfg.register(objects)
  } else if (p) {
    p.then(module => {
      //~ log.x(`imported ${modName}/${filename}`)
      cfg.register(getObjects(module))
      loadedPromises.push(true)
    })
    promises.push(p)
  }
  return [`%o ${filename}`, size(objects)]
}

const loadModules = () => {
  log.info('loading modules')
  moduleNames.forEach(modName => {
    try {
      let msgs = []
      for (let [filename, cfg] of items(registrations)) {
        if (cfg.isActive[modName]) {
          let msg = loadModule(modName, filename, cfg)
          msgs = [...msgs, msg]
        }
      }

      let msg = msgs
        .filter(e => e)
        .map(e => e[0])
        .join(', ')
      let values = msgs.filter(e => e).map(e => e[1])

      log.startup(`%o: register ${msg}`, modName, ...values)
    } catch (e) {
      log.exception('error during module load', e)
    }
  })
}

// #if process.env.UIX_PROD_CHUNKED !== 'true'
// // load in core chunk
// loadModules()
// #endif

// #if process.env.UIX_PROD_CHUNKED === 'true'
import {registerLoader} from 'uix/loadmodules'
const isReady = () => {
  return size(loadedPromises) === size(promises)
}
if (module.hot) {
  loadModules()
} else {
  registerLoader(loadModules, isReady)
}
// #endif
